import { LazyLoadImageProps } from 'react-lazy-load-image-component';
import classNames from 'classnames';
import { Colors, ColorsType, OpacityType, GradientsType } from '@vds/core';
import { LazyLoadWrapper } from '@components/utils';

import './TileBackground.scss';
export interface TileBackgroundProps {
  /** Any React.ReactNode element. This will be rendered on top of background. */
  content?: React.ReactNode;
  /** URL for the background image. */
  src?: string;
  /** Alternative text for the image. */
  alt?: string;
  /** Value of the opacity overlay. */
  opacity?: OpacityType;
  /** Color for the background. Used as background if no image or image fallback */
  color?: ColorsType | GradientsType;
  /** Should image use lazy loading */
  lazy?: boolean;
  /** Custom overrides for LazyLoadImage component */
  lazyProps?: LazyLoadImageProps;
  /** Optional classname for the component */
  className?: string;
}

/**
 * Tile Component
 * @param content - Any React.ReactNode element. This will be rendered on top of background.
 * @param src - URL for the background image.
 * @param alt - Alternative text for the image.
 * @param opacity - Value of the opacity overlay.
 * @param color - Color for the background. Used as background if no image or image fallback
 * @param lazy - should image use lazy loading
 * @param lazyProps - custom overrides for LazyLoadImage component
 * @param className - Optional classname for the component
 */
export const TileBackground: React.FC<TileBackgroundProps> = ({
  src,
  alt,
  opacity,
  color,
  lazy,
  lazyProps,
  content,
  className,
  ...rest
}) => {
  const tileBackgroundColorStyles = { background: `var(${color || Colors.BG.Dark})` };
  const tileBackgroundOpacityStyles = {
    opacity: opacity ? `var(--${opacity})` : 'var(--opacity-100)',
  };

  const fallback = <div className="vds-tile-background--color" style={tileBackgroundColorStyles} />;

  return (
    <div className={classNames('vds-tile-background', className)} {...rest}>
      <div
        className={classNames({ 'vds-tile-background--opacity': opacity })}
        style={tileBackgroundOpacityStyles}
      />
      <LazyLoadWrapper
        alt={alt}
        src={src}
        className="vds-tile-background--img"
        lazy={lazy}
        lazyProps={lazyProps}
        fallback={fallback}
      />
      {content && <div className="vds-tile-background__content">{content}</div>}
    </div>
  );
};
